import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  ShoppingBag, CreditCard, Banknote, CheckCircle, X, Plus, Minus,
  StickyNote, Loader2, Clock, Scissors, Check, Printer, Receipt,
  Pencil, Trash2, Search
} from 'lucide-react';
import axios from 'axios';
import { printKitchenTicket, printClientReceipt, printSplitReceipt } from '../../components/PrintTickets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ServerTableRecap = ({
  recapTableId, orders, server, startNewOrder,
  openPaymentForOrder, fetchData, formatPrice,
}) => {
  const [splitMode, setSplitMode] = useState(false);
  const [splitSelections, setSplitSelections] = useState({});
  const [splitPaymentMethod, setSplitPaymentMethod] = useState(null);
  const [splitCashAmount, setSplitCashAmount] = useState('');
  const [splitCashTip, setSplitCashTip] = useState('');
  const [splitCbTip, setSplitCbTip] = useState('');
  const [splitProcessing, setSplitProcessing] = useState(false);

  // Modify order state
  const [modifyMode, setModifyMode] = useState(false);
  const [modifyRemoveIndices, setModifyRemoveIndices] = useState([]);
  const [modifyAddItems, setModifyAddItems] = useState([]);
  const [modifyNote, setModifyNote] = useState('');
  const [modifySaving, setModifySaving] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [menuSearch, setMenuSearch] = useState('');
  const [showAddPicker, setShowAddPicker] = useState(false);

  // Fetch menu for add items
  const fetchMenu = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/menu`);
      setMenuItems((res.data || []).filter(i => i.is_active !== false));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (modifyMode && menuItems.length === 0) fetchMenu();
  }, [modifyMode, menuItems.length, fetchMenu]);

  const getSplitTotal = (items) => {
    return Object.entries(splitSelections).reduce((sum, [idx, qty]) => {
      const item = items[parseInt(idx)];
      if (item && qty > 0) return sum + item.price * qty;
      return sum;
    }, 0);
  };

  const toggleSplitItem = (idx, item) => {
    setSplitSelections(prev => {
      const current = prev[idx] || 0;
      const available = (item.quantity || 1) - (item.paid_qty || 0);
      if (current > 0) return { ...prev, [idx]: 0 };
      return { ...prev, [idx]: available };
    });
  };

  const updateSplitQty = (idx, delta, item) => {
    setSplitSelections(prev => {
      const current = prev[idx] || 0;
      const available = (item.quantity || 1) - (item.paid_qty || 0);
      const newQty = Math.max(0, Math.min(available, current + delta));
      return { ...prev, [idx]: newQty };
    });
  };

  const resetSplitMode = () => {
    setSplitMode(false); setSplitSelections({}); setSplitPaymentMethod(null);
    setSplitCashAmount(''); setSplitCashTip(''); setSplitCbTip(''); setSplitProcessing(false);
  };

  const resetModifyMode = () => {
    setModifyMode(false); setModifyRemoveIndices([]); setModifyAddItems([]);
    setModifyNote(''); setShowAddPicker(false); setMenuSearch('');
  };

  const processSplitPayment = async (orderId, items, method) => {
    const selected = Object.entries(splitSelections)
      .filter(([, qty]) => qty > 0)
      .map(([idx, qty]) => ({ item_index: parseInt(idx), qty_to_pay: qty }));
    if (selected.length === 0) return;
    setSplitProcessing(true);
    try {
      await axios.post(`${API}/server/${server.id}/split-payment/${orderId}`, {
        selected_items: selected, payment_method: method,
        cash_amount: method === 'cash' ? parseFloat(splitCashAmount) || 0 : 0,
        tip_amount: method === 'cash' ? parseFloat(splitCashTip) || 0 : parseFloat(splitCbTip) || 0,
      });
      const selectedItemDetails = selected.map(s => ({
        name: items[s.item_index]?.name, qty: s.qty_to_pay, price: items[s.item_index]?.price,
      }));
      printSplitReceipt({
        tableId: recapTableId, selectedItems: selectedItemDetails, splitTotal: getSplitTotal(items),
        paymentMethod: method, tip: method === 'cash' ? parseFloat(splitCashTip) || 0 : parseFloat(splitCbTip) || 0,
        orderId, createdAt: tableOrder?.created_at, serverName: server?.name,
      });
      resetSplitMode(); fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur lors du split');
    } finally { setSplitProcessing(false); }
  };

  const submitModification = async () => {
    if (!tableOrder) return;
    if (modifyRemoveIndices.length === 0 && modifyAddItems.length === 0) {
      resetModifyMode(); return;
    }
    setModifySaving(true);
    try {
      await axios.patch(`${API}/orders/${tableOrder.id}/modify`, {
        add_items: modifyAddItems.map(i => ({
          item_id: i.item_id, name: i.name, price: i.price, quantity: i.quantity,
        })),
        remove_item_indices: modifyRemoveIndices,
        note: modifyNote,
      });
      resetModifyMode(); fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur modification');
    } finally { setModifySaving(false); }
  };

  const toggleRemoveItem = (idx) => {
    setModifyRemoveIndices(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const addItemToModify = (menuItem) => {
    setModifyAddItems(prev => {
      const existing = prev.find(i => i.item_id === menuItem.id);
      if (existing) return prev.map(i => i.item_id === menuItem.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { item_id: menuItem.id, name: menuItem.name, price: menuItem.price, quantity: 1 }];
    });
  };

  const removeAddedItem = (itemId) => {
    setModifyAddItems(prev => prev.filter(i => i.item_id !== itemId));
  };

  const filteredMenu = menuItems.filter(i =>
    i.name.toLowerCase().includes(menuSearch.toLowerCase())
  ).slice(0, 20);

  const tableOrder = orders.find(o => o.table_id === recapTableId && !o.is_fully_paid);
  if (!tableOrder) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="text-center text-[#6E5E52]">
          <ShoppingBag className="w-14 h-14 mx-auto mb-3 opacity-20" />
          <p className="font-medium">Aucune commande sur cette table</p>
          <Button onClick={() => startNewOrder(recapTableId)}
            className="mt-4 bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl" data-testid="recap-new-order">
            <Plus className="w-4 h-4 mr-2" /> Prendre une commande
          </Button>
        </div>
      </div>
    );
  }

  const afterDiscount = tableOrder.total - (tableOrder.discount_amount || 0);
  const remaining = afterDiscount - (tableOrder.paid_amount || 0);
  const splitTotal = splitMode ? getSplitTotal(tableOrder.items) : 0;
  const hasSelection = splitMode && Object.values(splitSelections).some(q => q > 0);
  const hasModifyChanges = modifyRemoveIndices.length > 0 || modifyAddItems.length > 0;

  return (
    <div className="flex-1 overflow-y-auto" data-testid="table-recap-view">
      {/* Table header */}
      <div className="bg-[#0A422D] text-white px-4 py-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-2xl font-bold">Table {recapTableId}</p>
            <p className="text-white/60 text-sm">
              {tableOrder.items.reduce((s, i) => s + i.quantity, 0)} articles |{' '}
              <Clock className="w-3 h-3 inline" />{' '}
              {new Date(tableOrder.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </p>
            {tableOrder.notes && (
              <p className="text-white/50 text-xs mt-1 flex items-center gap-1">
                <StickyNote className="w-3 h-3" /> {tableOrder.notes}
              </p>
            )}
          </div>
          <div className="text-right">
            {splitMode && hasSelection ? (
              <>
                <p className="text-xs text-white/50">Selection</p>
                <p className="text-3xl font-bold text-[#D4A853]" data-testid="split-total">{formatPrice(splitTotal)}</p>
                <p className="text-xs text-white/40">Reste: {formatPrice(remaining - splitTotal)}</p>
              </>
            ) : (
              <>
                <p className="text-3xl font-bold text-[#D4A853]" data-testid="recap-total">{formatPrice(remaining)}</p>
                {tableOrder.discount_amount > 0 && (
                  <p className="text-xs text-white/50 line-through">{formatPrice(tableOrder.total)}</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modify mode banner */}
      {modifyMode && (
        <div className="bg-[#F59E0B]/10 border-b border-[#F59E0B]/30 px-4 py-2 flex items-center justify-between"
          data-testid="modify-mode-banner">
          <span className="text-sm font-medium text-[#F59E0B] flex items-center gap-2">
            <Pencil className="w-4 h-4" /> Mode modification
          </span>
          <button onClick={resetModifyMode} className="text-xs text-[#6E5E52] hover:text-[#2D241D]"
            data-testid="modify-cancel">Annuler</button>
        </div>
      )}

      {/* Split mode banner */}
      {splitMode && (
        <div className="bg-[#D4A853]/10 border-b border-[#D4A853]/30 px-4 py-2 flex items-center justify-between">
          <span className="text-sm font-medium text-[#D4A853] flex items-center gap-2">
            <Scissors className="w-4 h-4" /> Selectionnez les articles a encaisser
          </span>
          <button onClick={resetSplitMode} className="text-xs text-[#6E5E52] hover:text-[#2D241D]" data-testid="split-cancel">
            Annuler
          </button>
        </div>
      )}

      {/* Items list */}
      <div className="px-4 py-3">
        <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
          {tableOrder.items.map((item, idx) => {
            const paidQty = item.paid_qty || 0;
            const availableQty = (item.quantity || 1) - paidQty;
            const isFullyPaid = paidQty >= (item.quantity || 1);
            const selectedQty = splitSelections[idx] || 0;
            const isMarkedForRemoval = modifyMode && modifyRemoveIndices.includes(idx);

            return (
              <div key={idx}
                className={`flex items-center gap-3 px-4 py-3 ${idx > 0 ? 'border-t border-[#F3EDE5]' : ''} ${isFullyPaid ? 'opacity-40 bg-[#E8F5E9]/30' : ''} ${splitMode && selectedQty > 0 ? 'bg-[#D4A853]/5' : ''} ${isMarkedForRemoval ? 'bg-red-50 line-through opacity-50' : ''}`}
                data-testid={`recap-item-${idx}`}
                onClick={
                  modifyMode && !isFullyPaid ? () => toggleRemoveItem(idx)
                    : splitMode && !isFullyPaid ? () => toggleSplitItem(idx, item)
                      : undefined
                }
                style={(modifyMode || splitMode) && !isFullyPaid ? { cursor: 'pointer' } : {}}>

                {splitMode && (
                  <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all ${isFullyPaid ? 'border-gray-200 bg-gray-100' : selectedQty > 0 ? 'border-[#D4A853] bg-[#D4A853]' : 'border-[#E5DCD3]'}`}
                    data-testid={`split-check-${idx}`}>
                    {selectedQty > 0 && <Check className="w-4 h-4 text-white" />}
                  </div>
                )}

                {modifyMode && !isFullyPaid && (
                  <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all ${isMarkedForRemoval ? 'border-red-400 bg-red-400' : 'border-[#E5DCD3]'}`}
                    data-testid={`modify-check-${idx}`}>
                    {isMarkedForRemoval && <Trash2 className="w-3 h-3 text-white" />}
                  </div>
                )}

                <div className="flex-1 flex items-center gap-3 min-w-0">
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${isFullyPaid ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#E16833]/10 text-[#E16833]'}`}>
                    {item.quantity}x
                  </span>
                  <div className="min-w-0">
                    <span className={`text-sm font-medium ${isFullyPaid ? 'line-through text-[#6E5E52]' : isMarkedForRemoval ? 'text-red-400' : 'text-[#2D241D]'}`}>
                      {item.name}
                    </span>
                    {isFullyPaid && <p className="text-[10px] text-[#2D6A4F] font-medium">Paye</p>}
                    {paidQty > 0 && !isFullyPaid && <p className="text-[10px] text-[#D4A853]">{paidQty} deja paye</p>}
                  </div>
                </div>

                {splitMode && !isFullyPaid && availableQty > 1 && selectedQty > 0 && (
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => updateSplitQty(idx, -1, item)}
                      className="w-6 h-6 rounded-md bg-[#F3EDE5] flex items-center justify-center" data-testid={`split-minus-${idx}`}>
                      <Minus className="w-3 h-3 text-[#6E5E52]" />
                    </button>
                    <span className="w-5 text-center text-xs font-bold text-[#D4A853]">{selectedQty}</span>
                    <button onClick={() => updateSplitQty(idx, 1, item)}
                      className="w-6 h-6 rounded-md bg-[#D4A853] flex items-center justify-center" data-testid={`split-plus-${idx}`}>
                      <Plus className="w-3 h-3 text-white" />
                    </button>
                  </div>
                )}

                <span className={`text-sm font-bold ${isFullyPaid ? 'text-[#6E5E52]' : 'text-[#2D241D]'}`}>
                  {formatPrice(item.price * item.quantity)}
                </span>
              </div>
            );
          })}

          {/* Added items in modify mode */}
          {modifyMode && modifyAddItems.map((item, i) => (
            <div key={`add-${i}`} className="flex items-center gap-3 px-4 py-3 border-t border-[#F3EDE5] bg-[#E8F5E9]/30"
              data-testid={`modify-added-${i}`}>
              <div className="w-6 h-6 rounded-lg border-2 border-[#2D6A4F] bg-[#2D6A4F] flex items-center justify-center flex-shrink-0">
                <Plus className="w-3 h-3 text-white" />
              </div>
              <div className="flex-1 flex items-center gap-3 min-w-0">
                <span className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold bg-[#2D6A4F]/10 text-[#2D6A4F]">
                  {item.quantity}x
                </span>
                <span className="text-sm font-medium text-[#2D6A4F]">{item.name}</span>
              </div>
              <button onClick={() => removeAddedItem(item.item_id)}
                className="w-6 h-6 rounded-md bg-red-100 flex items-center justify-center text-red-500"
                data-testid={`modify-remove-added-${i}`}>
                <X className="w-3 h-3" />
              </button>
              <span className="text-sm font-bold text-[#2D6A4F]">{formatPrice(item.price * item.quantity)}</span>
            </div>
          ))}

          <div className="border-t-2 border-[#E5DCD3] px-4 py-3 bg-[#F3EDE5]">
            {tableOrder.discount_amount > 0 && (
              <div className="flex justify-between text-sm text-[#E63946] mb-1">
                <span>Remise</span><span>-{formatPrice(tableOrder.discount_amount)}</span>
              </div>
            )}
            {tableOrder.paid_amount > 0 && (
              <div className="flex justify-between text-sm text-[#2D6A4F] mb-1">
                <span>Deja paye</span><span>{formatPrice(tableOrder.paid_amount)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold text-[#2D241D]">
              <span>{splitMode && hasSelection ? 'Selection' : 'Reste a payer'}</span>
              <span className="text-[#E16833]">{formatPrice(splitMode && hasSelection ? splitTotal : remaining)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Add items picker in modify mode */}
      {modifyMode && showAddPicker && (
        <div className="px-4 pb-3" data-testid="modify-add-picker">
          <div className="bg-white rounded-2xl border border-[#2D6A4F] overflow-hidden">
            <div className="px-3 py-2 border-b border-[#E5DCD3] flex items-center gap-2">
              <Search className="w-4 h-4 text-[#6E5E52]" />
              <input type="text" value={menuSearch}
                onChange={(e) => setMenuSearch(e.target.value)}
                placeholder="Rechercher un plat..."
                className="flex-1 text-sm outline-none bg-transparent text-[#2D241D]"
                autoFocus data-testid="modify-search-input" />
              <button onClick={() => setShowAddPicker(false)}
                className="text-[#6E5E52] hover:text-[#2D241D]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {filteredMenu.map(item => {
                const alreadyAdded = modifyAddItems.find(i => i.item_id === item.id);
                return (
                  <button key={item.id} onClick={() => addItemToModify(item)}
                    className="w-full flex items-center justify-between px-4 py-2.5 border-b border-[#F3EDE5] last:border-0 hover:bg-[#F3EDE5] text-left"
                    data-testid={`modify-menu-item-${item.id}`}>
                    <span className="text-sm text-[#2D241D]">{item.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-[#E16833]">{item.price} THB</span>
                      {alreadyAdded && (
                        <span className="w-5 h-5 bg-[#2D6A4F] text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                          {alreadyAdded.quantity}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
              {filteredMenu.length === 0 && (
                <p className="text-center py-4 text-sm text-[#6E5E52]">Aucun resultat</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modify note */}
      {modifyMode && hasModifyChanges && (
        <div className="px-4 pb-2">
          <Input value={modifyNote} onChange={(e) => setModifyNote(e.target.value)}
            placeholder="Raison de la modification (optionnel)"
            className="rounded-xl border-[#F59E0B]/40 text-sm text-[#2D241D] bg-white"
            data-testid="modify-note-input" />
        </div>
      )}

      {/* Split payment overlay */}
      {splitMode && splitPaymentMethod && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="split-payment-overlay">
          <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-[#D4A853] text-black px-6 py-4">
              <p className="font-bold text-lg">Split - Table {recapTableId}</p>
              <p className="text-black/60 text-sm">
                {Object.entries(splitSelections).filter(([,q]) => q > 0).length} article(s) selectionne(s)
              </p>
              <p className="text-2xl font-bold mt-1" data-testid="split-overlay-total">{formatPrice(splitTotal)}</p>
            </div>

            {splitPaymentMethod === 'cash' && (
              <div className="p-6 space-y-4" data-testid="split-cash-form">
                <div className="text-center">
                  <Banknote className="w-10 h-10 mx-auto text-[#2D6A4F] mb-2" />
                  <p className="font-bold text-lg text-[#2D241D]">Paiement Cash (Split)</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2D241D] mb-1 block">Montant recu (THB)</label>
                  <Input type="number" value={splitCashAmount} onChange={(e) => setSplitCashAmount(e.target.value)}
                    className="rounded-xl border-[#E5DCD3] text-center text-xl font-bold h-14 text-[#2D241D] bg-white" data-testid="split-cash-amount" />
                  {parseFloat(splitCashAmount) > splitTotal && (
                    <p className="text-sm text-[#2D6A4F] text-center mt-2 font-medium" data-testid="split-cash-change">
                      Monnaie a rendre : {formatPrice(parseFloat(splitCashAmount) - splitTotal)}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2D241D] mb-1 block">Pourboire (optionnel)</label>
                  <Input type="number" value={splitCashTip} onChange={(e) => setSplitCashTip(e.target.value)}
                    placeholder="0" className="rounded-xl border-[#E5DCD3] text-[#2D241D] bg-white" data-testid="split-cash-tip" />
                </div>
                <div className="flex gap-3">
                  <Button onClick={() => setSplitPaymentMethod('_show_choice')} variant="outline"
                    className="flex-1 rounded-xl h-12 border-[#E5DCD3] text-[#2D241D] bg-white hover:bg-[#F3EDE5]">Retour</Button>
                  <Button onClick={() => processSplitPayment(tableOrder.id, tableOrder.items, 'cash')}
                    disabled={splitProcessing || !splitCashAmount || parseFloat(splitCashAmount) <= 0}
                    className="flex-1 bg-[#2D6A4F] hover:bg-[#1B4332] text-white rounded-xl h-12 font-semibold"
                    data-testid="split-confirm-cash">
                    {splitProcessing ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle className="w-5 h-5 mr-2" /> Encaisser</>}
                  </Button>
                </div>
              </div>
            )}

            {splitPaymentMethod === 'cb' && (
              <div className="p-6 space-y-4" data-testid="split-cb-form">
                <div className="text-center py-4">
                  <div className="w-20 h-20 rounded-2xl bg-[#6366F1]/10 flex items-center justify-center mx-auto mb-4">
                    <CreditCard className="w-10 h-10 text-[#6366F1] animate-pulse" />
                  </div>
                  <p className="font-bold text-xl text-[#2D241D] mb-2">CB Terminal (Split)</p>
                  <p className="text-sm text-[#6E5E52]">Montant: <strong className="text-[#6366F1]">{formatPrice(splitTotal)}</strong></p>
                  <div className="flex items-center justify-center gap-2 mt-3">
                    <Loader2 className="w-5 h-5 animate-spin text-[#6366F1]" />
                    <span className="text-sm text-[#6366F1] font-medium">Transaction en cours...</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2D241D] mb-1 block">Pourboire (optionnel)</label>
                  <Input type="number" value={splitCbTip} onChange={(e) => setSplitCbTip(e.target.value)}
                    placeholder="0" className="rounded-xl border-[#E5DCD3] text-[#2D241D] bg-white" data-testid="split-cb-tip" />
                </div>
                <div className="flex gap-3">
                  <Button onClick={() => setSplitPaymentMethod('_show_choice')}
                    variant="outline" className="flex-1 rounded-xl h-12 border-red-300 text-red-500 hover:bg-red-50">
                    <X className="w-4 h-4 mr-1" /> Echoue
                  </Button>
                  <Button onClick={() => processSplitPayment(tableOrder.id, tableOrder.items, 'card_terminal')}
                    disabled={splitProcessing}
                    className="flex-1 bg-[#6366F1] hover:bg-[#5558E6] text-white rounded-xl h-12 font-semibold"
                    data-testid="split-confirm-cb">
                    {splitProcessing ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle className="w-5 h-5 mr-2" /> Valide</>}
                  </Button>
                </div>
              </div>
            )}

            {splitPaymentMethod === '_show_choice' && (
              <div className="p-6 space-y-4">
                <p className="text-center font-semibold text-[#2D241D] text-lg">Mode de paiement</p>
                <div className="grid grid-cols-2 gap-4">
                  <button onClick={() => { setSplitPaymentMethod('cash'); setSplitCashAmount(String(splitTotal)); }}
                    className="flex flex-col items-center gap-3 p-6 rounded-2xl border-2 border-[#E5DCD3] hover:border-[#2D6A4F] hover:bg-[#2D6A4F]/5 transition-all active:scale-95"
                    data-testid="split-choose-cash">
                    <div className="w-16 h-16 rounded-2xl bg-[#2D6A4F]/10 flex items-center justify-center">
                      <Banknote className="w-8 h-8 text-[#2D6A4F]" />
                    </div>
                    <span className="font-bold text-[#2D241D] text-lg">Cash</span>
                  </button>
                  <button onClick={() => setSplitPaymentMethod('cb')}
                    className="flex flex-col items-center gap-3 p-6 rounded-2xl border-2 border-[#E5DCD3] hover:border-[#6366F1] hover:bg-[#6366F1]/5 transition-all active:scale-95"
                    data-testid="split-choose-cb">
                    <div className="w-16 h-16 rounded-2xl bg-[#6366F1]/10 flex items-center justify-center">
                      <CreditCard className="w-8 h-8 text-[#6366F1]" />
                    </div>
                    <span className="font-bold text-[#2D241D] text-lg">CB</span>
                  </button>
                </div>
                <button onClick={() => setSplitPaymentMethod(null)}
                  className="w-full text-center text-sm text-[#6E5E52] hover:text-[#2D241D] py-2">
                  Retour a la selection
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="px-4 pb-4 space-y-3">
        {/* Modify mode actions */}
        {modifyMode && (
          <div className="space-y-2">
            <Button onClick={() => setShowAddPicker(!showAddPicker)}
              variant="outline"
              className="w-full h-11 rounded-2xl border-[#2D6A4F] text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white font-semibold"
              data-testid="modify-add-btn">
              <Plus className="w-4 h-4 mr-2" /> Ajouter un article
            </Button>
            {hasModifyChanges && (
              <Button onClick={submitModification} disabled={modifySaving}
                className="w-full h-14 bg-[#F59E0B] hover:bg-[#D97706] text-black rounded-2xl text-base font-semibold"
                data-testid="modify-confirm-btn">
                {modifySaving ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Confirmer la modification
                    {modifyRemoveIndices.length > 0 && ` (-${modifyRemoveIndices.length})`}
                    {modifyAddItems.length > 0 && ` (+${modifyAddItems.reduce((s, i) => s + i.quantity, 0)})`}
                  </>
                )}
              </Button>
            )}
          </div>
        )}

        {remaining > 0 && !splitMode && !modifyMode && (
          <>
            <Button onClick={() => openPaymentForOrder(tableOrder, 'checkout')}
              className="w-full h-14 bg-[#0A422D] hover:bg-[#083323] text-white rounded-2xl text-base font-semibold"
              data-testid="recap-pay-btn">
              <Banknote className="w-5 h-5 mr-2" /> Encaisser ({formatPrice(remaining)})
            </Button>
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={() => { setSplitMode(true); setSplitSelections({}); }}
                className="h-12 rounded-2xl bg-[#D4A853] hover:bg-[#C49A48] text-black font-semibold"
                data-testid="split-bill-btn">
                <Scissors className="w-4 h-4 mr-2" /> Splitter
              </Button>
              <Button onClick={() => setModifyMode(true)}
                variant="outline"
                className="h-12 rounded-2xl border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black font-semibold"
                data-testid="modify-order-btn">
                <Pencil className="w-4 h-4 mr-2" /> Modifier
              </Button>
            </div>
          </>
        )}

        {splitMode && hasSelection && !splitPaymentMethod && (
          <div className="grid grid-cols-2 gap-3">
            <Button onClick={() => setSplitPaymentMethod('_show_choice')}
              className="h-14 bg-[#D4A853] hover:bg-[#C49A48] text-black rounded-2xl text-base font-semibold"
              data-testid="split-pay-btn">
              <Banknote className="w-5 h-5 mr-2" /> Encaisser ({formatPrice(splitTotal)})
            </Button>
            <Button onClick={resetSplitMode} variant="outline"
              className="h-14 rounded-2xl border-[#E5DCD3] text-[#6E5E52] hover:bg-[#F3EDE5] font-semibold"
              data-testid="split-cancel-btn">
              <X className="w-5 h-5 mr-2" /> Annuler
            </Button>
          </div>
        )}

        {remaining <= 0 && (
          <div className="bg-[#E8F5E9] rounded-2xl py-4 flex items-center justify-center gap-2 text-[#2D6A4F] font-semibold text-lg">
            <CheckCircle className="w-5 h-5" /> Paye
          </div>
        )}

        {!modifyMode && !splitMode && (
          <Button onClick={() => { resetSplitMode(); startNewOrder(recapTableId); }} variant="outline"
            className="w-full h-12 rounded-2xl border-[#D4A853] text-[#D4A853] hover:bg-[#D4A853] hover:text-white font-semibold"
            data-testid="recap-add-items">
            <Plus className="w-4 h-4 mr-2" /> Ajouter des articles
          </Button>
        )}

        {/* Print buttons */}
        {!modifyMode && !splitMode && (
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => printKitchenTicket({ tableId: recapTableId, items: tableOrder.items, notes: tableOrder.notes, orderId: tableOrder.id, createdAt: tableOrder.created_at, serverName: server?.name })}
              className="h-10 rounded-2xl border border-[#D4A853] text-[#D4A853] flex items-center justify-center gap-2 text-sm font-medium hover:bg-[#D4A853] hover:text-white transition-colors"
              data-testid="recap-print-kitchen">
              <Printer className="w-4 h-4" /> Imprimer Cuisine
            </button>
            <button onClick={() => printClientReceipt({ tableId: recapTableId, items: tableOrder.items, total: tableOrder.total, discountAmount: tableOrder.discount_amount, paymentMethod: tableOrder.payment_method, orderId: tableOrder.id, createdAt: tableOrder.created_at, serverName: server?.name })}
              className="h-10 rounded-2xl border border-[#0A422D] text-[#0A422D] flex items-center justify-center gap-2 text-sm font-medium hover:bg-[#0A422D] hover:text-white transition-colors"
              data-testid="recap-print-receipt">
              <Receipt className="w-4 h-4" /> Imprimer Ticket
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
